/**
 * FauxDS SizeToggle - Square toggle button for size selection
 *
 * ⚠️ This is a SIMPLIFIED RECREATION for prototyping.
 * It is NOT production code.
 *
 * Created from Figma design specs:
 * - Figma node: 353:8724 (Size picker)
 * - Square shape with rounded corners
 * - Black border on active state
 * - Gray border on inactive state
 */
import React, { forwardRef } from "react";

export interface SizeToggleProps {
  /**
   * The label text to display
   * @default "Label"
   */
  label?: string;
  /**
   * Whether the toggle is selected/active
   * @default false
   */
  active?: boolean;
  /**
   * Whether the toggle is disabled
   * @default false
   */
  disabled?: boolean;
  /**
   * Click handler
   */
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  /**
   * Button type attribute
   * @default "button"
   */
  type?: "button" | "submit" | "reset";
  /**
   * Optional className for additional styling
   */
  className?: string;
  /**
   * Accessibility label for screen readers
   * @default label
   */
  accessibilityLabel?: string;
}

/**
 * SizeToggle - Square toggle button for size selection
 *
 * A square button with rounded corners designed for size selection.
 * Features a black border when active and gray border when inactive.
 */
const SizeToggle = forwardRef<HTMLButtonElement, SizeToggleProps>(
  (
    {
      label = "Label",
      active = false,
      disabled = false,
      onClick,
      type = "button",
      className = "",
      accessibilityLabel,
    },
    ref
  ) => {
    const ariaLabel = accessibilityLabel || label;

    return (
      <button
        ref={ref}
        type={type}
        onClick={onClick}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-pressed={active}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '40px',
          minWidth: '40px',
          padding: '0 12px',
          backgroundColor: 'var(--slate-color-surface-primary)',
          border: active
            ? '1px solid var(--slate-color-action-border-default)' // #333 black border when active
            : '1px solid var(--slate-color-border-muted)', // #dfe0e1 gray border when inactive
          borderRadius: 'var(--slate-radius-component-default)', // 4px
          fontFamily: 'var(--slate-typography-family-sans)',
          fontSize: 'var(--slate-typography-font-size-200)',
          fontWeight: active
            ? 'var(--slate-typography-weight-medium)' // Medium weight when active
            : 'var(--slate-typography-weight-regular)', // Regular weight when inactive
          lineHeight: 'var(--slate-typography-line-height-200)',
          letterSpacing: 'var(--slate-typography-letter-spacing-200)',
          color: 'var(--slate-color-text-primary)',
          cursor: disabled ? 'not-allowed' : 'pointer',
          transition: 'border-color 0.15s ease-in-out',
          opacity: disabled ? 0.5 : 1,
        }}
        className={className}
        onMouseEnter={(e) => {
          if (!disabled && !active) {
            e.currentTarget.style.borderColor = 'var(--slate-color-action-border-hover)';
          }
        }}
        onMouseLeave={(e) => {
          if (!disabled && !active) {
            e.currentTarget.style.borderColor = 'var(--slate-color-border-muted)';
          }
        }}
      >
        {label}
      </button>
    );
  }
);

SizeToggle.displayName = "SizeToggle";

export default SizeToggle;
